/* eslint-disable @typescript-eslint/no-unused-vars */
import { ExclamationCircleIcon } from '@heroicons/react/16/solid';
import { removeWords, useDebounce, withErrorBoundary } from '@navi-app/utils';
import { ThemeProviderProps, useStyletron } from 'baseui';
import { Button, KIND, SIZE } from 'baseui/button';
import { Card } from 'baseui/card';
import { Input, SIZE as InputSIZE } from 'baseui/input';
import {
  COUNTRIES,
  PhoneInput,
  SIZE as SIZE_PHONEINPUT,
} from 'baseui/phone-input';
import { Select, StyledValueContainer } from 'baseui/select';
import { Textarea } from 'baseui/textarea';
import { TimePicker } from 'baseui/timepicker';
import { HeadingXSmall, LabelMedium, ParagraphSmall } from 'baseui/typography';
import { TFunction } from 'i18next';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { TFormLocation, TLocationItem, TParamSearch } from '../types/location';
import { useGetCities, useGetDistricts } from './hooks/use-location-query';
import useCreateLocationStore from './stores/create-location.store';

type TErrorSection = ThemeProviderProps;

type TTranslate = {
  t: TFunction<string, unknown>;
};

type TPhoneCountry = (typeof COUNTRIES)[keyof typeof COUNTRIES];

const DEFAULT_PHONE_COUNTRY: TPhoneCountry = COUNTRIES.ID;

const MandatorySection = ({ theme, t }: TErrorSection & TTranslate) => {
  return (
    <ParagraphSmall className="pt-2 !text-red-500">
      <div className="flex flex-row items-center">
        <ExclamationCircleIcon className="w-[14px] h-[14px] mr-1" />
        <div>{t('required')}</div>
      </div>
    </ParagraphSmall>
  );
};

const FormLocation = ({
  onOpenMap,
  selectedLocation,
  formikMaps,
  t,
}: TFormLocation) => {
  const [css, theme] = useStyletron();
  const geocoding = useMemo(
    () => selectedLocation?.properties?.geocoding,
    [selectedLocation]
  );

  const [isOpenSelectCity, setIsOpenSelectCity] = useState(false);
  const [isOpenSelectDistrict, setIsOpenSelectDistrict] = useState(false);
  const [isCityAssigned, setIsCityAssigned] = useState(false);
  const [isDistrictAssigned, setIsDistrictAssigned] = useState(false);
  const [selectedCity, setSelectedCity] = useState<Array<TLocationItem>>([]);
  const [selectedDistrict, setSelectedDistrict] = useState<
    Array<TLocationItem>
  >([]);
  const [picPhoneCountry, setPicPhoneCountry] = useState<TPhoneCountry>(
    DEFAULT_PHONE_COUNTRY
  );
  const [cityKeyword, setCityKeyword] = useState(
    selectedLocation?.city?.name ||
      selectedLocation?.city ||
      selectedLocation?.admin?.level5 ||
      geocoding?.city ||
      ''
  );
  const [districtKeyword, setDistrictKeyword] = useState(
    selectedLocation?.district?.name ||
      selectedLocation?.district ||
      selectedLocation?.admin?.level6 ||
      geocoding?.district ||
      ''
  );

  const keywordDebounceCity = useDebounce(cityKeyword, 300);
  const keywordDebounceDistrict = useDebounce(districtKeyword, 500);
  const { touched, errors } = formikMaps;
  const isErrorName = !!(touched?.name && errors?.name);
  const isErrorAddress = !!(touched?.address && errors?.address);
  const isCityError = !!(touched?.city && errors?.city);
  const isDistrictError = !!(touched?.district && errors?.district);
  const isErrorOpenTime = !!(
    (touched?.openTime || touched?.closeTime) &&
    errors?.openTime
  );
  const isErrorCloseTime = !!(
    (touched?.openTime || touched?.closeTime) &&
    errors?.closeTime
  );

  const {
    onSetData,
    name,
    cityId,
    districtId,
    address,
    picName,
    picPhoneNumber,
    openTime,
    closeTime,
    onReset,
  } = useCreateLocationStore(
    useShallow((state) => ({
      onSetData: state.onSetData,
      name: state.name,
      cityId: state.cityId,
      districtId: state.districtId,
      address: state.address,
      picName: state.picName,
      picPhoneNumber: state.picPhoneNumber,
      longitude: state.longitude,
      latitude: state.latitude,
      openTime: state.openTime,
      closeTime: state.closeTime,
      cities: state.cities,
      districts: state.districts,
      onReset: state.onReset,
    }))
  );

  const { dataDistricts, refetchDistrict, isLoadingDistrict } = useGetDistricts(
    {
      payload: { search: districtKeyword, cityId },
      onSuccess: (resp) => {
        if (keywordDebounceDistrict && !resp?.length) {
          setDistrictKeyword('');
          refetchDistrict();
        }
        if (resp?.length) {
          const firstItem = resp?.[0] as TLocationItem;
          if (!isDistrictAssigned) {
            setIsDistrictAssigned(true);
            if (firstItem?.id) {
              setSelectedDistrict([firstItem]);
              onSetData(firstItem?.id, 'districtId');
            }
          } else {
            setSelectedDistrict([]);
            onSetData(undefined, 'districtId');
          }
        } else {
          setIsDistrictAssigned(true);
          setSelectedDistrict([]);
          onSetData(undefined, 'districtId');
        }
      },
    }
  );

  const { isLoadingCities, dataCities, refetchCities } = useGetCities({
    payload: { search: cityKeyword as string },
    onSuccess: (data) => {
      if (data?.length) {
        if (isCityAssigned) {
          setDistrictKeyword('');
        }
        if (isDistrictAssigned) {
          setDistrictKeyword('');
        }
        const firstItem = data?.[0] as TLocationItem;
        if (!isCityAssigned) {
          setIsCityAssigned(true);
          if (firstItem?.id) {
            setSelectedCity([firstItem]);
            onSetData(firstItem?.id, 'cityId');
          }
        }
      } else {
        setIsCityAssigned(true);
        setSelectedCity([]);
        onSetData(undefined, 'cityId');
      }
    },
  });

  const handleChangeFormik = useCallback(
    (id: string, value: unknown) => {
      formikMaps.handleChange({
        target: { id, value },
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [formikMaps.handleChange]
  );

  useEffect(() => {
    // NOTE: add more exlude words here
    const excludeWords = ['kecamatan', 'kabupaten'];
    if (selectedLocation?.admin?.level6) {
      setDistrictKeyword(
        removeWords(selectedLocation?.admin?.level6 as string, excludeWords)
      );
    }
  }, [selectedLocation?.admin?.level6]);

  useEffect(() => {
    handleChangeFormik('district', districtId || '');
  }, [districtId, handleChangeFormik]);

  const onHandleSet = useCallback(
    (value: unknown, key: string) => {
      return onSetData(value, key);
    },
    [onSetData]
  );

  useEffect(() => {
    if (cityId) {
      handleChangeFormik('city', cityId);
    } else {
      handleChangeFormik('city', '');
      setSelectedDistrict([]);
      onHandleSet(null, 'districtId');
    }
  }, [cityId, handleChangeFormik, onHandleSet]);

  useEffect(() => {
    onHandleSet(selectedLocation?.label, 'address');
    handleChangeFormik('address', selectedLocation?.label);
  }, [onHandleSet, selectedLocation?.label, handleChangeFormik]);

  useEffect(() => {
    setTimeout(() => {
      refetchCities();
    }, 500);
  }, [keywordDebounceCity]);

  useEffect(() => {
    if (cityId) {
      setTimeout(() => {
        refetchDistrict();
      }, 500);
    }
  }, [cityId, keywordDebounceDistrict, refetchDistrict]);

  useEffect(() => {
    return () => {
      onReset();
    };
  }, [onReset]);

  const onInputCityChange = (e: ChangeEvent<HTMLInputElement>) => {
    setCityKeyword(e?.target?.value);
  };
  const onInputDistrictChange = (e: ChangeEvent<HTMLInputElement>) => {
    setDistrictKeyword(e?.target?.value);
  };

  const onChangeName = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    formikMaps.handleChange(e);
    onHandleSet(e.target.value, 'name');
  };

  const onChangeAddress = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    formikMaps.handleChange(e);
    onHandleSet(e.target.value, 'address');
  };

  const onChangePicName = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    formikMaps.handleChange(e);
    onHandleSet(e.target.value, 'picName');
  };

  const onChangePicPhoneNumber = (value: string) => {
    handleChangeFormik('picPhoneNumber', value);
    onHandleSet(value, 'picPhoneNumber');
  };

  const onChangeCity = (params: TParamSearch) => {
    setSelectedCity(params?.value as TLocationItem[]);
    handleChangeFormik('city', params?.value?.[0]?.id);
    onHandleSet(params?.value?.[0]?.id, 'cityId');
    setDistrictKeyword('');
  };

  const onChangeDistrict = (params: TParamSearch) => {
    handleChangeFormik('district', params?.value?.[0]?.id);
    setSelectedDistrict(params?.value as TLocationItem[]);
    onHandleSet(params?.value?.[0]?.id, 'districtId');
  };

  const onOpenSelectCity = () => setIsOpenSelectCity(true);
  const onOpenSelectDistrict = () => setIsOpenSelectDistrict(true);

  const onChangeOpenTime = (e: Date | null) => onHandleSet(e, 'openTime');
  const onChangeCloseTime = (e: Date | null) => onHandleSet(e, 'closeTime');

  const TimePickerOverride = {
    Select: {
      props: {
        overrides: {
          ValueContainer: {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            component: (props: any) => (
              <div className="flex items-center justify-between w-full">
                <StyledValueContainer {...props}>
                  {props.children}
                </StyledValueContainer>
                <LabelMedium className="mr-2">WIB</LabelMedium>
              </div>
            ),
          },
        },
      },
    },
  };

  return (
    <>
      <Card
        overrides={{
          Root: { style: { marginTop: '16px', marginBottom: '32px' } },
        }}
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center">
          <div className="flex grow items-center">
            <span>
              <b>{selectedLocation?.title}</b> - {selectedLocation?.description}
            </span>
          </div>
          <div className="flex-none min-w-[68px] py-1 sm:px-2 sm:pl-3">
            <Button
              onClick={onOpenMap}
              kind={KIND.secondary}
              size={SIZE.compact}
            >
              {t('change_point')}
            </Button>
          </div>
        </div>
      </Card>

      <HeadingXSmall className="pb-4">{t('address')}</HeadingXSmall>
      <div className="flex pb-2">
        <LabelMedium>{t('location_name')}</LabelMedium>
        <LabelMedium className="ml-2 !text-red-500">{'*'}</LabelMedium>
      </div>
      <Input
        id="name"
        error={isErrorName}
        value={name}
        onChange={onChangeName}
        placeholder="Masukkan nama lokasi"
        clearOnEscape
      />
      {isErrorName && <MandatorySection theme={theme} t={t} />}

      <div className="grid grid-cols-1 md:grid-cols-2 pt-3 ">
        <div className="basis-1/2 grow-0 pr-0 md:pr-2">
          <div className="flex pb-2 pt-2">
            <LabelMedium>{t('city_disctrict')}</LabelMedium>
            <LabelMedium className="ml-2 !text-red-500">{'*'}</LabelMedium>
          </div>

          <Select
            id="city"
            isLoading={isLoadingCities}
            error={isCityError}
            onInputChange={onInputCityChange}
            options={dataCities}
            overrides={{
              Popover: {
                props: { overrides: { Body: { style: { zIndex: 10 } } } },
              },
            }}
            value={selectedCity}
            placeholder={t('city_disctrict')}
            onChange={onChangeCity}
            onOpen={onOpenSelectCity}
          />
          {isCityError && <MandatorySection theme={theme} t={t} />}
        </div>

        <div className="basis-1/2 grow-0 pl-0 md:pl-2">
          <div className="flex pb-2 pt-2">
            <LabelMedium>{t('district')}</LabelMedium>
            <LabelMedium className="ml-2 !text-red-500">{'*'}</LabelMedium>
          </div>
          <Select
            id="district"
            isLoading={isLoadingDistrict}
            onInputChange={onInputDistrictChange}
            error={isDistrictError}
            disabled={isLoadingCities || !cityId}
            options={dataDistricts}
            overrides={{
              Popover: {
                props: { overrides: { Body: { style: { zIndex: 10 } } } },
              },
            }}
            value={selectedDistrict}
            placeholder={t('district')}
            onChange={onChangeDistrict}
            onOpen={onOpenSelectDistrict}
          />
          {isDistrictError && <MandatorySection theme={theme} t={t} />}
        </div>
      </div>

      <div className="flex mb-2 mt-6">
        <LabelMedium>{t('location_detail')}</LabelMedium>
        <LabelMedium className="ml-2 !text-red-500">{'*'}</LabelMedium>
      </div>

      <div className="mb-4">
        <Textarea
          error={isErrorAddress}
          id="address"
          overrides={{ Root: { style: { width: '100%' } } }}
          size={InputSIZE.compact}
          resize="vertical"
          value={address}
          onChange={onChangeAddress}
          placeholder={t('enter_location_detail')}
          clearOnEscape
        />
        {isErrorAddress && <MandatorySection theme={theme} t={t} />}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 mb-8">
        <div className="basis-1/2 grow-0 pr-0 md:pr-2">
          <div className="flex pb-2 pt-2">
            <LabelMedium>Nama PIC</LabelMedium>
          </div>
          <Input
            id="picName"
            value={picName}
            onChange={onChangePicName}
            placeholder="Masukkan nama PIC"
            clearOnEscape
          />
        </div>

        <div className="basis-1/2 grow-0 pl-0 md:pl-2">
          <div className="flex pb-2 pt-2">
            <LabelMedium>No. Telepon</LabelMedium>
          </div>
          <PhoneInput
            clearable={false}
            country={picPhoneCountry}
            onCountryChange={({ option }) => {
              setPicPhoneCountry(
                (option as TPhoneCountry) || DEFAULT_PHONE_COUNTRY
              );
            }}
            text={picPhoneNumber}
            onTextChange={(e) => {
              const formatted = e.target.value?.replace(/^0+(?=\d)/, '');
              onChangePicPhoneNumber(formatted);
            }}
            size={SIZE_PHONEINPUT.default}
            placeholder="Masukkan no. telepon"
          />
        </div>
      </div>

      <HeadingXSmall className="mb-4">{t('operational_hours')}</HeadingXSmall>
      <div className="flex flex-col sm:flex-row items-start gap-4 w-full mb-4">
        <div className="flex flex-col gap-1 basis-1/2">
          <LabelMedium>{t('open_time')}</LabelMedium>
          <TimePicker
            overrides={TimePickerOverride}
            placeholder="00:00"
            format="24"
            step={300}
            value={openTime}
            onChange={onChangeOpenTime}
            nullable
            error={isErrorOpenTime}
          />
          {isErrorOpenTime && <MandatorySection theme={theme} t={t} />}
        </div>
        <div className="flex flex-col gap-1 basis-1/2">
          <LabelMedium>{t('close_time')}</LabelMedium>
          <TimePicker
            overrides={TimePickerOverride}
            placeholder="00:00"
            format="24"
            step={300}
            value={closeTime}
            onChange={onChangeCloseTime}
            nullable
            error={isErrorCloseTime}
          />
          {isErrorCloseTime && <MandatorySection theme={theme} t={t} />}
        </div>
      </div>
    </>
  );
};

export default withErrorBoundary(FormLocation);
